import { range } from "lodash";
import { CalibrationNodeFragment } from "types/graphql-schema";

import { getRatingScaleClassName } from "@apps/ratings/helpers";
import Avatars from "@components/avatar/avatars";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { AssessmentAnswerWithResponderAndId } from "./questions/types";

const AssessmentQuestionRangeOptionsCombined = ({
  answers,
  calibrations,
  startValue,
  endValue,
  labels,
  labelDescriptions,
}: {
  answers: AssessmentAnswerWithResponderAndId[];
  startValue: number;
  endValue: number;
  labels: ReadonlyArray<string | null>;
  labelDescriptions: ReadonlyArray<string | null>;
  calibrations?: CalibrationNodeFragment[];
}) => {
  return (
    <div
      className={classNames(
        `gap-2 grid`,
        getRatingScaleClassName(startValue, endValue)
      )}
    >
      {range(startValue, endValue + 1).map((value, index) => {
        const itemAnswers = answers.filter(
          (answer) => answer.integerAnswer === value
        );
        const itemAnswerIds = itemAnswers.map((answer) => answer.id);
        const itemCalibrations = calibrations
          ? calibrations.filter(
              (calibration) =>
                calibration.answerId &&
                itemAnswerIds.includes(calibration.answerId) &&
                !calibration.isSuggestion
            )
          : [];
        const intialAnswer =
          itemCalibrations.length > 0
            ? itemCalibrations[itemCalibrations.length - 1].prevIntegerAnswer
            : value;
        return (
          <Tooltip
            key={value}
            text={
              labelDescriptions[index]
                ? `<div>${labelDescriptions[index]
                    ?.split("\n")
                    .join("<br/>")}</div>`
                : undefined
            }
          >
            <div
              contentEditable={false}
              className={classNames(
                "flex flex-col gap-1.5 items-center cursor-default rounded p-3",
                itemAnswers.length === 0 && "border border-gray-100 bg-gray-50",
                itemAnswers.length > 0 && "bg-blue-100"
              )}
            >
              <div
                className={classNames(
                  "text-sm font-semibold w-full text-center tracking-tighter leading-tight",
                  itemAnswers.length === 0 && "text-gray-500",
                  itemAnswers.length > 0 && "text-gray-700"
                )}
              >
                {labels[index] || value}{" "}
              </div>
              <div>
                {itemAnswers.length > 0 ? (
                  <Avatars
                    showNullAsAnonymous
                    users={itemAnswers.map((answer) => answer.responder)}
                    size={5}
                    max={3}
                    modalTitle="Responders"
                  />
                ) : (
                  <TopicflowIcon
                    type="radioButtonUnchecked"
                    className="h-4 w-4 text-gray-500"
                  />
                )}
              </div>
              {value !== intialAnswer && (
                <Tooltip text="This answer has been modified">
                  <div className="font-semibold text-amber-700">*</div>
                </Tooltip>
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default AssessmentQuestionRangeOptionsCombined;
